import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Reveal } from '../components/Reveal';
import { Calendar, User as UserIcon, Clock, ArrowLeft, Share2, Facebook, Linkedin, Twitter, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { fetchArticle, fetchArticles } from '../lib/api';
import '../styles/rich-editor.css';

/**
 * Normalise les espaces insécables générés par les éditeurs (Word/Google Docs/Quill
 * copier-coller) qui empêchent la justification du texte. Conserve les nbsp
 * typographiques français légitimes (avant `:!?;»` et après `«`).
 */
function normalizeArticleHtml(html = '') {
  if (!html) return '';
  // 1) Ne conserver &nbsp; que devant une ponctuation française (`:!?;»`) et après `«`.
  //    Ailleurs, on les remplace par une espace normale afin de restaurer la justification.
  let out = html.replace(/\u00A0(?![:!?;»])/g, ' ');
  // 2) Les entités déjà décodées comme &nbsp; textuelles → même traitement
  out = out.replace(/&nbsp;(?![:!?;»])/g, ' ');
  return out;
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchArticle(slug)
      .then((data) => setPost(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    fetchArticles({ limit: 4 }).then((data) => setRelated((data || []).filter((p) => p.slug !== slug).slice(0, 3)));
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <section className="pt-40 pb-20 text-center">
        <Loader2 className="w-8 h-8 text-sky-500 animate-spin mx-auto" />
      </section>
    );
  }

  if (notFound || !post) {
    return (
      <section className="pt-40 pb-20 text-center">
        <h1 className="font-display text-3xl text-sky-950 mb-4">Article introuvable</h1>
        <Link to="/blog"><Button>Retour au blog</Button></Link>
      </section>
    );
  }

  return (
    <>
      <article className="pt-32 pb-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Link to="/blog" className="inline-flex items-center gap-2 text-sky-700 hover:text-sky-900 font-medium text-sm mb-8">
              <ArrowLeft className="w-4 h-4" />Retour au blog
            </Link>
            <div className="flex items-center gap-3 text-xs mb-5">
              <span className="px-3 py-1 rounded-full bg-sky-50 text-sky-700 font-bold">{post.category}</span>
              <span className="text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" />{post.readTime}</span>
            </div>
            <h1 className="font-display font-extrabold text-4xl lg:text-5xl text-sky-950 leading-tight mb-6">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 pb-8 border-b border-sky-100">
              <span className="flex items-center gap-2"><UserIcon className="w-4 h-4" />{post.author}</span>
              <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />{post.date}</span>
              <div className="flex items-center gap-2 ml-auto">
                <Share2 className="w-4 h-4 text-slate-400" />
                <a href="#" className="w-8 h-8 rounded-lg bg-sky-50 hover:bg-sky-100 flex items-center justify-center"><Facebook className="w-3.5 h-3.5 text-sky-700" /></a>
                <a href="#" className="w-8 h-8 rounded-lg bg-sky-50 hover:bg-sky-100 flex items-center justify-center"><Twitter className="w-3.5 h-3.5 text-sky-700" /></a>
                <a href="#" className="w-8 h-8 rounded-lg bg-sky-50 hover:bg-sky-100 flex items-center justify-center"><Linkedin className="w-3.5 h-3.5 text-sky-700" /></a>
              </div>
            </div>
          </Reveal>
        </div>

        {post.image && (
          <Reveal>
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 my-10">
              <div className="aspect-[16/9] rounded-3xl overflow-hidden shadow-xl">
                <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
        )}

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            {post.excerpt && <p className="text-xl text-slate-700 leading-relaxed mb-8 font-medium">{post.excerpt}</p>}
            <div
              className="prose prose-lg prose-slate max-w-none prose-headings:font-display prose-headings:text-sky-950 prose-a:text-sky-600 prose-strong:text-sky-950 prose-blockquote:border-emerald-400 prose-blockquote:text-slate-700 prose-img:rounded-xl article-content"
              data-testid="article-content"
            >
              {/\<[a-z][\s\S]*\>/i.test(post.content || '') ? (
                <div dangerouslySetInnerHTML={{ __html: normalizeArticleHtml(post.content) }} />
              ) : (
                (post.content || '').split('\n\n').map((para, i) => (
                  <p key={i} className="text-slate-700 leading-relaxed mb-5">{para}</p>
                ))
              )}
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-12 p-8 rounded-3xl bg-gradient-to-br from-sky-50 to-emerald-50 border border-sky-100 text-center">
              <h3 className="font-display font-extrabold text-2xl text-sky-950 mb-3">Soutenez notre action</h3>
              <p className="text-slate-600 mb-5">Votre don aide à financer l'installation de nouveaux Kiosques à Eau dans toute la France.</p>
              <Link to="/soutenir"><Button className="bg-gradient-to-r from-sky-500 to-emerald-500 text-white rounded-xl px-6">Faire un don</Button></Link>
            </div>
          </Reveal>
        </div>
      </article>

      {related.length > 0 && (
        <section className="py-20 bg-sky-50/40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display font-extrabold text-3xl text-sky-950 mb-8 text-center">À lire aussi</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((p, i) => (
                <Reveal key={p.id} delay={i * 80}>
                  <Link to={`/blog/${p.slug}`} className="group block">
                    <article className="rounded-3xl overflow-hidden bg-white border border-sky-100 hover:shadow-lg transition-all">
                      <div className="aspect-[16/10] overflow-hidden">
                        {p.image && <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                      </div>
                      <div className="p-5">
                        <span className="text-xs font-semibold text-sky-700">{p.category}</span>
                        <h3 className="font-display font-bold text-base text-sky-950 mt-1 line-clamp-2 group-hover:text-sky-700 transition-colors">{p.title}</h3>
                      </div>
                    </article>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
